'use client';

import { useDisclosure } from '@nextui-org/react';
import { debounce } from 'lodash';
import React, { useEffect, useState } from 'react';
import { toast } from 'react-toastify';

import ClientOnly from '@/components/client-only';
import Attendance from '@/contents/attendance/Attendance';
import Referral from '@/contents/attendance/Referral';
import { useAuth } from '@/provider/UserProvider';
import { claimAttendance, historyClaimAttendance } from '@/services/attendance';
import { getReferralList } from '@/services/provider';
import { useSQuery } from '@/utils';

export interface Attendances {
  isToday: Boolean;
  checkEd: boolean;
  isEndDay: boolean;
  date: string;
}

export interface ListReferral {
  referralList: any[];
  airiLockReferral: any[];
  totalAiriEarnedFromReferrals: number;
  totalAiriTransferred: number;
}

const DailyEarn = ({}) => {
  const { auth, isLoginClient, setAuth } = useAuth();
  const disclosure = useDisclosure();
  const [attendances, setAttendances] = useState<Attendances[]>();
  const [tab, setTab] = useState<'Referral' | 'Attendance'>('Referral');

  const { data: historyAttendance, isSuccess: isSuccessAttendance } = useSQuery({
    queryKey: ['history-attendance'],
    queryFn: historyClaimAttendance,
    keepPreviousData: true,
    staleTime: 0,
    enabled: isLoginClient,
  });
  const { data: referralList } = useSQuery({
    queryKey: ['referral-list'],
    queryFn: () => getReferralList(auth.user.publicAddress),
    keepPreviousData: true,
    staleTime: 0,
    enabled: Boolean(isLoginClient && auth.user?.publicAddress && auth.user?.isEmailVerified),
  });

  const sevenDayBefore = (date = new Date()) => {
    const dates = [];
    for (let i = 0; i < 7; i++) {
      const dateTemp = new Date(date);
      dateTemp.setDate(dateTemp.getDate() + i);
      dates.push(dateTemp.toISOString().split('T')[0]); // Format as YYYY-MM-DD
    }
    return dates;
  };

  const handleClaimDaily = debounce(
    async () => {
      if (!isSuccessAttendance) return toast.error('Get list history attendance failed!');
      try {
        if (!isLoginClient) return disclosure.onOpen();
        const res = await claimAttendance();
        if (res.status === 201) {
          setAttendances(
            (sheet) => sheet?.map((item) => (item.isToday ? { ...item, checkEd: true } : item)),
          );
          setAuth({
            ...auth,
            user: { ...auth.user, credit: auth.user.credit + (res.data?.credit || 0) },
          });
          toast.success('10 credits has been added to your account.');
        }
      } catch (error) {
        console.error('Error claiming attendance:', error);
      }
    },
    1000,
    { leading: true, trailing: false },
  );

  useEffect(() => {
    const attendanceSheet = historyAttendance?.attendanceSheet;
    if (attendanceSheet?.length > 0 && isLoginClient) {
      const sevenDay = sevenDayBefore(attendanceSheet.at(-1).createdAt);
      const attendancesTemp = sevenDay.map(
        (date, index) =>
          ({
            checkEd: index + 1 <= attendanceSheet?.length,
            date,
            isEndDay: index + 1 === sevenDay.length,
            isToday: date && new Date().getDate() === new Date(date).getDate(),
          }) as Attendances,
      );
      setAttendances(attendancesTemp);
    } else {
      const sevenDay = sevenDayBefore();
      const attendancesTemp = sevenDay.map(
        (date, index) =>
          ({
            checkEd: false,
            date,
            isEndDay: index + 1 === sevenDay.length,
            isToday: date && new Date().getDate() === new Date(date).getDate(),
          }) as Attendances,
      );
      setAttendances(attendancesTemp);
    }
  }, [historyAttendance, isLoginClient]);

  return (
    <ClientOnly>
      <div className="flex flex-col">
        <div className="main-container mt-4 flex w-full max-w-[1008px] items-center rounded-full bg-gray-50 p-[6px]">
          <p
            aria-checked={tab === 'Referral'}
            onClick={() => setTab('Referral')}
            className="w-1/2 cursor-pointer rounded-full py-2 text-center text-14 font-semibold aria-checked:border-1 aria-checked:border-gray-100 aria-checked:bg-white"
          >
            Referral
          </p>
          <p
            aria-checked={tab === 'Attendance'}
            onClick={() => setTab('Attendance')}
            className="w-1/2 cursor-pointer rounded-full py-2 text-center text-14 font-semibold aria-checked:border-1 aria-checked:border-gray-100 aria-checked:bg-white"
          >
            Attendance
          </p>
        </div>
        {tab === 'Attendance' ? (
          <Attendance
            handleClaimDaily={handleClaimDaily}
            attendances={attendances}
            historyAttendance={historyAttendance}
            disclosure={disclosure}
          />
        ) : (
          <Referral referralList={referralList} />
        )}
      </div>
    </ClientOnly>
  );
};

export default DailyEarn;
